/**
 * Klaviyo Adapter
 *
 * Plain object implementing IntegrationAdapter — no class, no inheritance.
 * Uses safeFetch() for all external API calls.
 *
 * ============================================================================
 * CRITICAL: Klaviyo Reporting API Rate Limit (Story 12.8)
 * ============================================================================
 *
 * The Klaviyo Reporting API is capped at **225 requests/day per OAuth app
 * instance**, with a steady rate of 2 req/min (1 req/s burst). This is by far
 * the most restrictive rate limit in the entire ProvenLeap integration suite.
 *
 * Do NOT attempt to "fix" the cache TTL or add force-refresh code paths for
 * Reporting API calls. Aggressive caching (1-6h via fetchMetricsWithCache) is
 * what keeps us under the cap. A cheap per-integration daily KV counter
 * (`klaviyo:quota:{integrationId}:{YYYY-MM-DD}`) tracks consumption and
 * short-circuits Reporting API calls at 200/225 (with 25-call buffer for retries
 * and race conditions). See `getReportingQuotaUsage`, `incrementReportingQuota`,
 * `checkReportingQuotaBudget` in `./api.ts`.
 *
 * Per-fetch budget:
 *   - 1 mapped-metric lookup (cached 24h; typically a cache hit)
 *   - 1 campaign-values-reports
 *   - 1 flow-values-reports
 *   - 1 form-values-reports
 *   - 1 segment-values-reports
 *   - 2 reserve for retries / pagination
 *   = 7 calls per fetch max → ~32 full fetches/day/tenant, adequate with 6h cache
 *
 * ============================================================================
 * Record Type Contract (Story 12.8)
 * ============================================================================
 *
 * Reporting API records (`campaign`, `flow`, `form`, `segment`) are aggregated
 * over the requested date window — **NOT daily**. Daily time-series remains
 * in `dailyOverview` records from metric-aggregates. Do NOT add `date` fields
 * to Reporting API records — consumers rely on `_reportType` as the discriminator
 * for time-series vs totals. A dev-mode runtime assertion at the end of
 * fetchMetrics() enforces this contract to catch regressions.
 *
 * ============================================================================
 * Klaviyo API Specifics
 * ============================================================================
 * - OAuth 2.0 with PKCE, Bearer token auth
 * - API revision header required: "revision: 2026-01-15"
 * - metric-aggregates: uses event time (doesn't match UI) — good for daily totals
 * - Reporting API (/values-reports/): matches UI (send date) — good for per-entity stats
 * - Metric IDs are account-scoped: resolve name → ID via GET /api/metrics/, cache 24h
 * - Access tokens expire — refresh token support required
 * - Rate limits per tier:
 *     metric-aggregates: S-tier (3 req/s burst, 60/min steady)
 *     get metrics:       M-tier (10 req/s, 150/min)
 *     /values-reports:   S+daily (1/s burst, 2/min, 225/day) ← binding constraint
 *
 * Breaking change: "Unsubscribed" metric renamed to "Clicked email to unsubscribe"
 *
 * ADR-013: Adapter Pattern for API Isolation
 * ADR-017: Retry with exponential backoff and Retry-After header respect
 */

import type {
  DimensionDefinition,
  IntegrationAdapter,
  AdapterContext,
  OAuthCredentials,
  AdapterResult,
  AdapterErrorCode,
  AdapterMetrics,
  AccountInfo,
  MetricDefinition,
  RefreshedTokens,
} from "../adapter";
import { adapterOk, adapterErr } from "../adapter";
import { sanitizeErrorMessage } from "../safe-fetch";
import type { KVStore } from "../cache";
import {
  fetchMetricsList,
  fetchMetricAggregates,
  fetchKlaviyoAccountsWithDetail,
  fetchMappedMetric,
  fetchCampaignValuesReport,
  fetchFlowValuesReport,
  fetchFormValuesReport,
  fetchSegmentValuesReport,
  checkReportingQuotaBudget,
  getReportingQuotaUsage,
  // Story IMC-25: Tags, A/B variations, segment profile count
  fetchTagsForCampaign,
  fetchCampaignsWithMessages,
  fetchSegmentProfileCount,
  type KlaviyoCampaignReportEntry,
  type KlaviyoFlowReportEntry,
  type KlaviyoFormReportEntry,
  type KlaviyoSegmentReportEntry,
  type KlaviyoTag,
  type KlaviyoCampaignMeta,
} from "./api";
import { KlaviyoQuotaExhaustedError } from "./errors";
import { refreshKlaviyoToken, KLAVIYO_SCOPES } from "./oauth";

// ============================================================================
// Metric Fetch Config (metric-aggregates event → ProvenLeap key mapping)
// ============================================================================
//
// The /api/metric-aggregates/ endpoint returns daily totals by event type.
// Each event we care about gets one entry here. Story 12.8 expanded from the
// 9 baseline events in Story 12.7 to 19 events covering SMS, e-commerce funnel,
// and spam feedback.
//
// IMPORTANT: Uses "Clicked email to unsubscribe" (renamed from "Unsubscribed").

const METRIC_FETCH_CONFIG: Array<{
  klaviyoName: string;
  measurements: string[];
  keys: Record<string, string>; // measurement → ProvenLeap key
}> = [
  // Email delivery + engagement (baseline from Story 12.7)
  {
    klaviyoName: "Received Email",
    measurements: ["count"],
    keys: { count: "emails_received" },
  },
  {
    klaviyoName: "Delivered Email",
    measurements: ["count"],
    keys: { count: "emails_delivered" },
  },
  {
    klaviyoName: "Opened Email",
    measurements: ["count"],
    keys: { count: "emails_opened" },
  },
  {
    klaviyoName: "Clicked Email",
    measurements: ["count"],
    keys: { count: "emails_clicked" },
  },
  {
    klaviyoName: "Bounced Email",
    measurements: ["count"],
    keys: { count: "emails_bounced" },
  },
  {
    klaviyoName: "Clicked email to unsubscribe",
    measurements: ["count"],
    keys: { count: "unsubscribed" },
  },
  {
    klaviyoName: "Placed Order",
    measurements: ["count", "sum_value"],
    keys: { count: "conversions", sum_value: "revenue" },
  },
  {
    klaviyoName: "Received SMS",
    measurements: ["count"],
    keys: { count: "sms_received" },
  },
  {
    klaviyoName: "Clicked SMS",
    measurements: ["count"],
    keys: { count: "sms_clicked" },
  },
  // Story 12.8: SMS expansion (5 new events)
  {
    klaviyoName: "Sent SMS",
    measurements: ["count"],
    keys: { count: "sms_sent" },
  },
  {
    klaviyoName: "Unsubscribed from SMS",
    measurements: ["count"],
    keys: { count: "sms_unsubscribed" },
  },
  {
    klaviyoName: "Failed to Deliver SMS",
    measurements: ["count"],
    keys: { count: "sms_failed" },
  },
  {
    klaviyoName: "Consented to Receive SMS",
    measurements: ["count"],
    keys: { count: "sms_consented" },
  },
  // Story 12.8: Spam feedback (event-time signal distinct from campaign spam_complaints)
  {
    klaviyoName: "Marked Email as Spam",
    measurements: ["count"],
    keys: { count: "spam_complaints_event" },
  },
  // Story 12.8: E-commerce funnel (5 new events)
  {
    klaviyoName: "Checkout Started",
    measurements: ["count"],
    keys: { count: "checkout_started" },
  },
  {
    klaviyoName: "Added to Cart",
    measurements: ["count"],
    keys: { count: "added_to_cart" },
  },
  {
    klaviyoName: "Viewed Product",
    measurements: ["count"],
    keys: { count: "viewed_product" },
  },
  {
    klaviyoName: "Ordered Product",
    measurements: ["count"],
    keys: { count: "ordered_product" },
  },
  {
    klaviyoName: "Active on Site",
    measurements: ["count"],
    keys: { count: "active_on_site" },
  },
];

// ============================================================================
// Metric & Dimension Definitions (per Integration Mapping Reference, Story 12.8)
// ============================================================================
//
// 63 metrics spanning 11 categories. Covers:
// - Email delivery, engagement, bounces, unsubscribes (metric-aggregates + reporting API)
// - SMS sends, delivery, engagement, consent, financial stats (with null pass-through)
// - Revenue, conversions, e-commerce funnel (checkout/cart/product)
// - Campaign & flow statistics from the Reporting API (recipients, rates, ROI)
// - Form analytics (views, submits, step-level)
// - Segment growth (total, added, removed, net change)
// - List size (semi-static, populated from accountMetadata.listTotalMembers)

export const KLAVIYO_METRICS: MetricDefinition[] = [
  // ---- Email Delivery (3) ----
  { key: "emails_received", label: "Emails Received", type: "number", category: "Email Delivery", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "bar-chart"] },
  { key: "emails_delivered", label: "Emails Delivered", type: "number", category: "Email Delivery", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "bar-chart"] },
  { key: "delivery_rate", label: "Delivery Rate", type: "percentage", category: "Email Delivery", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { numerator: "emails_delivered", denominator: "emails_received" }, compatibleWidgets: ["kpi", "gauge", "line-chart", "goal"] },

  // ---- Email Engagement (5) ----
  { key: "emails_opened", label: "Emails Opened", type: "number", category: "Email Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "bar-chart"] },
  { key: "open_rate", label: "Open Rate", type: "percentage", category: "Email Engagement", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { numerator: "emails_opened", denominator: "emails_delivered" }, compatibleWidgets: ["kpi", "gauge", "line-chart", "goal"] },
  { key: "emails_clicked", label: "Emails Clicked", type: "number", category: "Email Engagement", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "bar-chart"] },
  { key: "click_rate", label: "Click Rate", type: "percentage", category: "Email Engagement", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { numerator: "emails_clicked", denominator: "emails_delivered" }, compatibleWidgets: ["kpi", "gauge", "line-chart", "goal"] },
  { key: "click_to_open_rate", label: "Click-to-Open Rate", type: "percentage", category: "Email Engagement", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { numerator: "emails_clicked", denominator: "emails_opened" }, compatibleWidgets: ["kpi", "gauge"] },

  // ---- Bounces (3) ----
  { key: "emails_bounced", label: "Emails Bounced", type: "number", category: "Bounces", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "data-table"] },
  { key: "bounce_rate", label: "Bounce Rate", type: "percentage", category: "Bounces", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { numerator: "emails_bounced", denominator: "emails_received" }, compatibleWidgets: ["kpi", "gauge", "line-chart"] },
  { key: "bounced_or_failed", label: "Bounced or Failed", type: "number", category: "Bounces", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "data-table"] },

  // ---- Delivery Failures (2) ----
  { key: "failed", label: "Failed Deliveries", type: "number", category: "Bounces", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "data-table"] },
  { key: "failed_rate", label: "Failed Rate", type: "percentage", category: "Bounces", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { numerator: "failed", denominator: "emails_received" }, compatibleWidgets: ["kpi", "gauge"] },

  // ---- Unsubscribes (3) ----
  { key: "unsubscribed", label: "Unsubscribed", type: "number", category: "Unsubscribes", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"] },
  { key: "unsubscribe_uniques", label: "Unique Unsubscribes", type: "number", category: "Unsubscribes", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "unsubscribe_rate", label: "Unsubscribe Rate", type: "percentage", category: "Unsubscribes", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { numerator: "unsubscribed", denominator: "emails_received" }, compatibleWidgets: ["kpi", "gauge", "line-chart"] },

  // ---- Spam & Abuse (3) ----
  { key: "spam_complaints", label: "Spam Complaints", type: "number", category: "Spam", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "data-table"] },
  { key: "spam_complaint_rate", label: "Spam Complaint Rate", type: "percentage", category: "Spam", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { numerator: "spam_complaints", denominator: "emails_received" }, compatibleWidgets: ["kpi", "gauge"] },
  { key: "spam_complaints_event", label: "Marked as Spam (Event)", type: "number", category: "Spam", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"] },

  // ---- SMS Delivery (3) ----
  { key: "sms_sent", label: "SMS Sent", type: "number", category: "SMS", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"] },
  { key: "sms_received", label: "SMS Received", type: "number", category: "SMS", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"] },
  { key: "sms_failed", label: "SMS Failed", type: "number", category: "SMS", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },

  // ---- SMS Engagement (3) ----
  { key: "sms_clicked", label: "SMS Clicked", type: "number", category: "SMS", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"] },
  { key: "sms_unsubscribed", label: "SMS Unsubscribed", type: "number", category: "SMS", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "sms_consented", label: "SMS Consented", type: "number", category: "SMS", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"] },

  // ---- SMS Financial (4 — null before Oct 2025) ----
  { key: "message_segment_count_sum", label: "SMS Segment Count", type: "number", category: "SMS", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "data-table"] },
  { key: "text_message_credit_usage_amount", label: "SMS Credit Usage", type: "number", category: "SMS", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "text_message_spend", label: "SMS Spend", type: "currency", category: "SMS", dataType: "Currency", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"] },
  { key: "text_message_roi", label: "SMS ROI", type: "number", category: "SMS", dataType: "Decimal", aggregationKind: "ratio", ratioComponents: { denominator: "text_message_spend" }, compatibleWidgets: ["kpi", "gauge"] },

  // ---- Revenue & Conversions (7) ----
  { key: "conversions", label: "Conversions (Placed Order)", type: "number", category: "Revenue", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "bar-chart"] },
  { key: "conversion_uniques", label: "Unique Conversions", type: "number", category: "Revenue", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "conversion_rate", label: "Conversion Rate", type: "percentage", category: "Revenue", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { numerator: "conversions", denominator: "emails_received" }, compatibleWidgets: ["kpi", "gauge"] },
  { key: "conversion_value", label: "Conversion Value (Revenue)", type: "currency", category: "Revenue", dataType: "Currency", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "bar-chart"] },
  { key: "revenue", label: "Revenue", type: "currency", category: "Revenue", dataType: "Currency", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "bar-chart"] },
  { key: "average_order_value", label: "Avg Order Value", type: "currency", category: "Revenue", dataType: "Currency", aggregationKind: "ratio", ratioComponents: { numerator: "revenue", denominator: "conversions" }, compatibleWidgets: ["kpi", "line-chart"] },
  { key: "revenue_per_recipient", label: "Revenue per Recipient", type: "currency", category: "Revenue", dataType: "Currency", aggregationKind: "ratio", ratioComponents: { numerator: "revenue", denominator: "recipients" }, compatibleWidgets: ["kpi"] },

  // ---- E-commerce Funnel (5) ----
  { key: "viewed_product", label: "Viewed Product", type: "number", category: "E-commerce Funnel", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "funnel"] },
  { key: "added_to_cart", label: "Added to Cart", type: "number", category: "E-commerce Funnel", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "funnel"] },
  { key: "checkout_started", label: "Checkout Started", type: "number", category: "E-commerce Funnel", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "funnel"] },
  { key: "ordered_product", label: "Ordered Product", type: "number", category: "E-commerce Funnel", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "funnel"] },
  { key: "active_on_site", label: "Active on Site", type: "number", category: "E-commerce Funnel", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"] },

  // ---- Campaign Reporting (3 — report-level only, not event-based) ----
  { key: "recipients", label: "Recipients", type: "number", category: "Campaign Reporting", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "data-table"] },
  { key: "delivered", label: "Delivered", type: "number", category: "Campaign Reporting", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "data-table"] },
  { key: "opens", label: "Opens (Total)", type: "number", category: "Campaign Reporting", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "data-table"] },
  { key: "opens_unique", label: "Unique Opens", type: "number", category: "Campaign Reporting", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "data-table"] },
  { key: "clicks", label: "Clicks (Total)", type: "number", category: "Campaign Reporting", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "data-table"] },
  { key: "clicks_unique", label: "Unique Clicks", type: "number", category: "Campaign Reporting", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "data-table"] },
  { key: "bounced", label: "Bounced (Reporting)", type: "number", category: "Campaign Reporting", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "data-table"] },
  { key: "unsubscribes", label: "Unsubscribes (Reporting)", type: "number", category: "Campaign Reporting", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "data-table"] },

  // ---- Form Analytics (9) ----
  { key: "viewed_form", label: "Form Views", type: "number", category: "Forms", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"] },
  { key: "viewed_form_uniques", label: "Unique Form Views", type: "number", category: "Forms", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "submits", label: "Form Submissions", type: "number", category: "Forms", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"] },
  { key: "submit_rate", label: "Submit Rate", type: "percentage", category: "Forms", dataType: "Percentage", aggregationKind: "ratio", ratioComponents: { numerator: "submits", denominator: "viewed_form" }, compatibleWidgets: ["kpi", "gauge"] },
  { key: "closed_form", label: "Form Closed", type: "number", category: "Forms", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "closed_form_uniques", label: "Unique Form Closers", type: "number", category: "Forms", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "qualified_form", label: "Qualified Visitors", type: "number", category: "Forms", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "viewed_form_step", label: "Form Step Views", type: "number", category: "Forms", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },
  { key: "submitted_form_step", label: "Form Step Submissions", type: "number", category: "Forms", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi"] },

  // ---- Segment Growth (4) ----
  { key: "total_members", label: "Total Members", type: "number", category: "Segments", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"] },
  { key: "members_added", label: "Members Added", type: "number", category: "Segments", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "bar-chart"] },
  { key: "members_removed", label: "Members Removed", type: "number", category: "Segments", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart"] },
  { key: "net_members_changed", label: "Net Members Changed", type: "number", category: "Segments", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "bar-chart"] },

  // ---- List (2) ----
  { key: "list_total_members", label: "List Members", type: "number", category: "List", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "goal"] },
  { key: "emails_sent", label: "Emails Sent", type: "number", category: "Email Delivery", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "line-chart", "bar-chart"] },

  // ---- Segment Profile Count (1, Story IMC-25) ----
  { key: "segment_profile_count", label: "Segment Profile Count", type: "number", category: "Segments", dataType: "Integer", aggregationKind: "additive", compatibleWidgets: ["kpi", "data-table", "goal"] },
];

export const KLAVIYO_DIMENSIONS: DimensionDefinition[] = [
  // Time
  { key: "date", label: "Date", type: "Time" },

  // Campaign reporting dimensions
  { key: "campaignId", label: "Campaign ID", type: "Content" },
  { key: "campaignMessageId", label: "Campaign Message ID", type: "Content" },
  { key: "campaignName", label: "Campaign Name", type: "Content" },
  { key: "campaignMessageName", label: "Campaign Message Name", type: "Content" },
  { key: "sendChannel", label: "Send Channel", type: "Content" },
  { key: "sendTime", label: "Send Time", type: "Time" },

  // Flow reporting dimensions
  { key: "flowId", label: "Flow ID", type: "Content" },
  { key: "flowMessageId", label: "Flow Message ID", type: "Content" },
  { key: "flowName", label: "Flow Name", type: "Content" },
  { key: "flowMessageName", label: "Flow Message Name", type: "Content" },

  // Form reporting dimensions
  { key: "formId", label: "Form ID", type: "Content" },
  { key: "formVersionId", label: "Form Version ID", type: "Content" },

  // Segment reporting dimensions
  { key: "segmentId", label: "Segment ID", type: "Content" },
  { key: "segmentName", label: "Segment Name", type: "Content" },

  // Reserved for future metric-aggregates grouping
  { key: "attributedMessage", label: "Attributed Message", type: "Content" },
  { key: "attributedFlow", label: "Attributed Flow", type: "Content" },

  // ---- Story IMC-25: 9 new dimensions ----
  // A/B test variation dimensions
  { key: "variation", label: "Variation ID", type: "Content" },
  { key: "variationName", label: "Variation Name", type: "Content" },

  // Tag dimensions
  { key: "tagId", label: "Tag ID", type: "Content" },
  { key: "tagName", label: "Tag Name", type: "Content" },

  // SMS format
  { key: "textMessageFormat", label: "SMS Format", type: "Content" },

  // Attribution
  { key: "attributedChannel", label: "Attributed Channel", type: "Content" },

  // Email domain
  { key: "emailDomain", label: "Email Domain", type: "Audience" },

  // Click URL
  { key: "url", label: "Clicked URL", type: "Content" },

  // Time interval grouping
  { key: "interval", label: "Time Interval", type: "Time" },
];

// ============================================================================
// Helper: Derive computed rates
// ============================================================================

function deriveRates(totals: Record<string, number>): Record<string, number> {
  const delivered = totals.emails_delivered || 0;
  const received = totals.emails_received || 0;

  return {
    open_rate: delivered > 0 ? (totals.emails_opened || 0) / delivered : 0,
    click_rate: delivered > 0 ? (totals.emails_clicked || 0) / delivered : 0,
    bounce_rate: received > 0 ? (totals.emails_bounced || 0) / received : 0,
    unsubscribe_rate: delivered > 0 ? (totals.unsubscribed || 0) / delivered : 0,
    delivery_rate: received > 0 ? delivered / received : 0,
  };
}

// ============================================================================
// Helper: Build a report record from a Reporting API entry (Story 12.8)
// ============================================================================
//
// IMPORTANT: Spreads statistics with null pass-through. SMS financial fields
// (text_message_spend, text_message_roi, etc.) may be null for pre-Oct 2025
// data and contracted plans — do NOT coerce null to 0, that would mask
// missing-data vs zero-data in reports.
//
// Does NOT add a `date` field — Reporting API records are window totals, not
// daily time-series. The `_reportType` discriminator is the contract, and a
// runtime assertion at the end of fetchMetrics() enforces this.

/** Story IMC-25: Enrichment data for campaign records (A/B variations + tags) */
interface CampaignEnrichment {
  campaignMeta?: KlaviyoCampaignMeta;
  tags?: KlaviyoTag[];
}

function buildCampaignRecord(
  entry: KlaviyoCampaignReportEntry,
  enrichment?: CampaignEnrichment
): Record<string, unknown> {
  // Detect A/B variation: campaign with >1 message is an A/B test.
  // Match the campaign_message_id from the report entry to the campaign meta's messages.
  let variation: string | null = null;
  let variationName: string | null = null;
  const meta = enrichment?.campaignMeta;
  if (meta && meta.messages.length > 1) {
    const messageId = entry.groupings.campaign_message_id;
    const matched = messageId
      ? meta.messages.find((m) => m.id === messageId)
      : null;
    if (matched) {
      variation = matched.id;
      variationName = matched.label;
    }
  }

  // Tags
  const tags = enrichment?.tags ?? [];
  const tagId = tags.length > 0 ? tags.map((t) => t.id).join(",") : null;
  const tagName = tags.length > 0 ? tags.map((t) => t.name).join(",") : null;

  // SMS format detection from send_channel
  const sendChannel = entry.groupings.send_channel ?? "email";
  const textMessageFormat = sendChannel === "sms" ? "sms" : null;

  return {
    _reportType: "campaign",
    campaignId: entry.groupings.campaign_id ?? null,
    campaignMessageId: entry.groupings.campaign_message_id ?? null,
    campaignName: entry.groupings.campaign_message_name ?? entry.groupings.campaign_id ?? "Untitled Campaign",
    campaignMessageName: entry.groupings.campaign_message_name ?? null,
    sendChannel,
    sendTime: entry.groupings.send_time ?? null,
    // Story IMC-25: new dimensions
    variation,
    variationName,
    tagId,
    tagName,
    textMessageFormat,
    ...entry.statistics,
  };
}

function buildFlowRecord(entry: KlaviyoFlowReportEntry): Record<string, unknown> {
  return {
    _reportType: "flow",
    flowId: entry.groupings.flow_id ?? null,
    flowMessageId: entry.groupings.flow_message_id ?? null,
    flowName: entry.groupings.flow_name ?? entry.groupings.flow_id ?? "Untitled Flow",
    flowMessageName: entry.groupings.flow_message_name ?? null,
    sendChannel: entry.groupings.send_channel ?? "email",
    ...entry.statistics,
  };
}

function buildFormRecord(entry: KlaviyoFormReportEntry): Record<string, unknown> {
  return {
    _reportType: "form",
    formId: entry.groupings.form_id ?? null,
    formVersionId: entry.groupings.form_version_id ?? null,
    ...entry.statistics,
  };
}

function buildSegmentRecord(
  entry: KlaviyoSegmentReportEntry,
  profileCount?: number | null
): Record<string, unknown> {
  return {
    _reportType: "segment",
    segmentId: entry.groupings.segment_id ?? null,
    // H1 fix: Klaviyo returns segment_name in groupings — fall back to segment_id
    // when the API omits it (older API revisions or tests). Never duplicate segment_id.
    segmentName: entry.groupings.segment_name ?? entry.groupings.segment_id ?? null,
    // Story IMC-25: segment profile count from GET /api/segments/{id}?additional-fields[segment]=profile_count
    segment_profile_count: profileCount ?? null,
    ...entry.statistics,
  };
}

// ============================================================================
// Adapter Implementation
// ============================================================================

export const klaviyoAdapter: IntegrationAdapter = {
  platform: "klaviyo",

  rateLimits: {
    requestsPerMinute: 150, // Metrics tier — most-used endpoint in Story 12.7
    retryAfterMs: 1000,
  },

  /**
   * Fetches Klaviyo metrics across all record types.
   *
   * Record types emitted:
   *   - summary        — 1 record with totals + derived rates over the window
   *   - dailyOverview  — 1 record per day with daily counts (metric-aggregates, event time)
   *   - campaign       — 1 record per campaign (Reporting API, window total, send time)
   *   - flow           — 1 record per flow message (Reporting API, window total)
   *   - form           — 1 record per form (Reporting API, window total)
   *   - segment        — 1 record per segment (Reporting API, growth stats over window)
   *
   * IMPORTANT: campaign/flow/form/segment records are window TOTALS, not daily.
   * They intentionally do NOT have a `date` field. See JSDoc block at top of file.
   *
   * Graceful degradation: if the Reporting API quota is exhausted, or any single
   * report type fails, the adapter still returns summary + dailyOverview records
   * from the metric-aggregates endpoint. Partial failure > total failure.
   */
  async fetchMetrics(
    ctx: AdapterContext,
    credentials: OAuthCredentials,
    dateRange: { start: Date; end: Date }
  ): Promise<AdapterResult<AdapterMetrics>> {
    const kv = ctx.env.KV as KVStore;
    const token = credentials.accessToken;

    // Step 1: Resolve metric names → IDs
    let metricMap: Map<string, string>;
    try {
      metricMap = await fetchMetricsList(kv, token, ctx.tenantId, ctx.integrationId);
    } catch (err) {
      return adapterErr({
        code: "invalid_response",
        message: sanitizeErrorMessage(`Failed to fetch Klaviyo metrics list: ${err instanceof Error ? err.message : String(err)}`),
        retryable: true,
      });
    }

    // Step 2: Fetch aggregates for each metric (batched for performance)
    // Klaviyo allows 3 req/s on metric-aggregates (S-tier). Batch 5 at a time.
    const BATCH_SIZE = 5;
    const dailyData: Map<string, Record<string, number>> = new Map(); // date → { key: value }
    const totals: Record<string, number> = {};

    // All metric keys that could appear in daily records (for zero-filling)
    const allMetricKeys: string[] = [];
    for (const config of METRIC_FETCH_CONFIG) {
      for (const plKey of Object.values(config.keys)) {
        allMetricKeys.push(plKey);
      }
    }

    // Resolve metric IDs and prepare fetch tasks
    const fetchTasks: Array<{
      config: (typeof METRIC_FETCH_CONFIG)[number];
      metricId: string;
    }> = [];

    for (const config of METRIC_FETCH_CONFIG) {
      const metricId = metricMap.get(config.klaviyoName);
      if (!metricId) {
        for (const plKey of Object.values(config.keys)) {
          totals[plKey] = 0;
        }
        continue;
      }
      fetchTasks.push({ config, metricId });
    }

    // Execute in batches of BATCH_SIZE (parallel within batch, sequential between batches)
    for (let i = 0; i < fetchTasks.length; i += BATCH_SIZE) {
      const batch = fetchTasks.slice(i, i + BATCH_SIZE);
      const batchResults = await Promise.allSettled(
        batch.map((task) =>
          fetchMetricAggregates(token, task.metricId, dateRange, task.config.measurements)
        )
      );

      for (let j = 0; j < batchResults.length; j++) {
        const settled = batchResults[j]!;
        const task = batch[j]!;

        if (settled.status === "rejected") {
          return adapterErr({
            code: "invalid_response",
            message: sanitizeErrorMessage(`Failed to fetch Klaviyo metric "${task.config.klaviyoName}": ${settled.reason instanceof Error ? settled.reason.message : String(settled.reason)}`),
            retryable: true,
          });
        }

        const results = settled.value;
        for (const [measurement, plKey] of Object.entries(task.config.keys)) {
          const result = results[measurement];
          if (!result) {
            totals[plKey] = 0;
            continue;
          }

          let total = 0;
          for (let k = 0; k < result.dates.length; k++) {
            const dateStr = result.dates[k]!;
            const value = result.values[k] ?? 0;
            total += value;

            if (!dailyData.has(dateStr)) {
              dailyData.set(dateStr, {});
            }
            dailyData.get(dateStr)![plKey] = value;
          }
          totals[plKey] = total;
        }
      }
    }

    // Step 3: Derive computed rates
    const rates = deriveRates(totals);

    // Step 3.5: Derive `emails_sent` (not a Klaviyo event; approximated as delivered + bounced)
    const emailsSent = (totals.emails_delivered || 0) + (totals.emails_bounced || 0);

    // Step 3.6: `list_total_members` placeholder.
    //
    // This metric is exposed in KLAVIYO_METRICS for widget-config autocompletion,
    // but the end-to-end wire-up (fetching list `profile_count` at list-selection
    // time and persisting on `integrations.accountMetadata`) is deferred to a
    // follow-up story. For now we always emit 0 — the default Klaviyo Overview
    // preset does NOT use this metric (replaced by `emails_sent` in Story 12.8
    // code-review fix H2). Keeping the field ensures custom widget configs
    // referencing `list_total_members` don't crash on `undefined`.
    const listTotalMembers = 0;

    // Step 4: Build summary record
    const summaryRecord: Record<string, unknown> = {
      _reportType: "summary",
      ...totals,
      ...rates,
      emails_sent: emailsSent,
      list_total_members: listTotalMembers,
    };

    // Step 5: Build daily overview records (zero-fill missing metric keys)
    const dailyRecords: Record<string, unknown>[] = [];
    const sortedDates = Array.from(dailyData.keys()).sort();
    for (const dateStr of sortedDates) {
      const dayData = dailyData.get(dateStr)!;
      // Zero-fill any missing metric keys so all daily records have consistent shape
      for (const key of allMetricKeys) {
        if (!(key in dayData)) {
          dayData[key] = 0;
        }
      }
      const dayRates = deriveRates(dayData);
      const dayEmailsSent = (dayData.emails_delivered || 0) + (dayData.emails_bounced || 0);
      dailyRecords.push({
        _reportType: "dailyOverview",
        date: dateStr,
        ...dayData,
        ...dayRates,
        emails_sent: dayEmailsSent,
      });
    }

    // ========================================================================
    // Step 6 (Story 12.8): Reporting API records — campaign/flow/form/segment
    // ========================================================================
    //
    // Fetched only when the daily quota counter is healthy. Each report type
    // is wrapped in try/catch so one failure doesn't block the others
    // (partial degradation > total failure).

    const reportRecords: Record<string, unknown>[] = [];

    // Gate: check quota upfront
    let quotaStatus: "ok" | "warn" | "exhausted" = "ok";
    try {
      quotaStatus = await checkReportingQuotaBudget(kv, ctx.integrationId);
    } catch {
      // KV unavailable — proceed without Reporting API (safe)
      quotaStatus = "exhausted";
    }

    if (quotaStatus === "exhausted") {
      const count = await getReportingQuotaUsage(kv, ctx.integrationId).catch(() => 0);
      // Structured log for ops visibility
      // eslint-disable-next-line no-console
      console.info(JSON.stringify({
        level: "info",
        event: "klaviyo.reporting_quota.exhausted",
        integrationId: ctx.integrationId,
        count,
      }));
    } else {
      if (quotaStatus === "warn") {
        const count = await getReportingQuotaUsage(kv, ctx.integrationId).catch(() => 0);
        // eslint-disable-next-line no-console
        console.warn(JSON.stringify({
          level: "warn",
          event: "klaviyo.reporting_quota.warning",
          integrationId: ctx.integrationId,
          count,
        }));
      }

      // Resolve conversion_metric_id (cached 24h; typically a cache hit).
      // Campaign/flow/segment Reporting API endpoints REQUIRE this — Klaviyo
      // returns 400 if it's missing. Form reports never take it.
      const conversionMetricId = await fetchMappedMetric(
        kv,
        token,
        ctx.tenantId,
        ctx.integrationId
      );

      // H5 (round-2 review fix): if mapped-metric resolution failed, skip
      // campaign/flow/segment reports entirely — they would spam the Klaviyo
      // API with 400s and waste quota (up to 4 units/fetch × ~4 fetches/day
      // after cache TTL). Form reports still proceed since they don't need
      // conversion_metric_id. See Task 3.2 in the story spec.
      const skipReportsNeedingConversionMetric = conversionMetricId === null;
      if (skipReportsNeedingConversionMetric) {
        // eslint-disable-next-line no-console
        console.warn(JSON.stringify({
          level: "warn",
          event: "klaviyo.mapped_metric.unresolved",
          integrationId: ctx.integrationId,
          message: "Skipping campaign/flow/segment reports — no mapped revenue metric resolved",
        }));
      }

      // ====================================================================
      // Story IMC-25: Pre-fetch enrichment data (tags + campaign A/B meta)
      // ====================================================================
      // Campaign meta fetched first, then tags in batches of 3 to respect
      // rate limits (M-tier for campaigns, 3/s for tags). Failures are
      // non-fatal — records are still emitted without enrichment if these fail.
      let campaignMetaMap = new Map<string, KlaviyoCampaignMeta>();
      const tagCache = new Map<string, KlaviyoTag[]>(); // campaignId → tags

      if (!skipReportsNeedingConversionMetric) {
        try {
          campaignMetaMap = await fetchCampaignsWithMessages(kv, token, ctx.tenantId, ctx.integrationId);

          // Tag resolution: for each campaign in meta, fetch its tags.
          // Batch 3 at a time to respect tag endpoint rate limits (3/s burst).
          // Limit to first 15 campaigns total to stay well within 60/min steady.
          if (campaignMetaMap.size > 0) {
            const campaignIds = Array.from(campaignMetaMap.keys()).slice(0, 15);
            const TAG_BATCH_SIZE = 3;
            for (let bi = 0; bi < campaignIds.length; bi += TAG_BATCH_SIZE) {
              const batch = campaignIds.slice(bi, bi + TAG_BATCH_SIZE);
              const tagResults = await Promise.allSettled(
                batch.map((cid) => fetchTagsForCampaign(token, cid))
              );
              for (let ti = 0; ti < batch.length; ti++) {
                const tagResult = tagResults[ti];
                if (tagResult && tagResult.status === "fulfilled" && tagResult.value.length > 0) {
                  tagCache.set(batch[ti]!, tagResult.value);
                }
              }
            }
          }
        } catch {
          // Non-fatal — enrichment is best-effort
        }
      }

      // Fetch each report type in sequence with per-type error isolation.
      // Sequential (not parallel) — respects the 1 req/s Reporting API burst cap.
      const reportFetchers: Array<{
        name: string;
        fetch: () => Promise<Record<string, unknown>[]>;
      }> = [];

      if (!skipReportsNeedingConversionMetric) {
        reportFetchers.push({
          name: "campaign",
          fetch: async () => {
            const entries = await fetchCampaignValuesReport(
              kv,
              ctx.integrationId,
              token,
              conversionMetricId,
              dateRange,
            );
            return entries.map((entry) => {
              const campId = entry.groupings.campaign_id;
              const enrichment: CampaignEnrichment = {
                campaignMeta: campId ? campaignMetaMap.get(campId) : undefined,
                tags: campId ? tagCache.get(campId) : undefined,
              };
              return buildCampaignRecord(entry, enrichment);
            });
          },
        });
        reportFetchers.push({
          name: "flow",
          fetch: async () => {
            const entries = await fetchFlowValuesReport(
              kv,
              ctx.integrationId,
              token,
              conversionMetricId,
              dateRange,
            );
            return entries.map(buildFlowRecord);
          },
        });
      }

      // Form reports always run — they don't need conversion_metric_id
      reportFetchers.push({
        name: "form",
        fetch: async () => {
          const entries = await fetchFormValuesReport(
            kv,
            ctx.integrationId,
            token,
            dateRange,
          );
          return entries.map(buildFormRecord);
        },
      });

      if (!skipReportsNeedingConversionMetric) {
        reportFetchers.push({
          name: "segment",
          fetch: async () => {
            const entries = await fetchSegmentValuesReport(
              kv,
              ctx.integrationId,
              token,
              conversionMetricId,
              dateRange,
            );
            // Story IMC-25: Fetch profile counts for each segment.
            // Rate-limited (1/s burst, 15/m steady) — fetch sequentially with 1.1s delay.
            // Limit to 10 segments to stay well within 15/min steady limit.
            const segmentEntries = entries.slice(0, 10);
            const profileCounts = new Map<string, number | null>();
            for (let si = 0; si < segmentEntries.length; si++) {
              const segId = segmentEntries[si]!.groupings.segment_id;
              if (segId) {
                // Respect 1/s burst rate limit — delay between calls (skip first)
                if (si > 0) {
                  await new Promise((resolve) => setTimeout(resolve, 1100));
                }
                try {
                  const count = await fetchSegmentProfileCount(token, segId);
                  profileCounts.set(segId, count);
                } catch {
                  profileCounts.set(segId, null);
                }
              }
            }
            return entries.map((entry) => {
              const segId = entry.groupings.segment_id;
              const count = segId ? profileCounts.get(segId) ?? null : null;
              return buildSegmentRecord(entry, count);
            });
          },
        });
      }

      for (const fetcher of reportFetchers) {
        try {
          const records = await fetcher.fetch();
          reportRecords.push(...records);
        } catch (err) {
          if (err instanceof KlaviyoQuotaExhaustedError) {
            // Quota tripped mid-fetch; stop trying additional report types
            // eslint-disable-next-line no-console
            console.info(JSON.stringify({
              level: "info",
              event: "klaviyo.reporting_quota.mid_fetch_exhausted",
              integrationId: ctx.integrationId,
              failedAt: fetcher.name,
            }));
            break;
          }
          // Other errors: log and continue to the next report type (partial degradation)
          // eslint-disable-next-line no-console
          console.warn(JSON.stringify({
            level: "warn",
            event: "klaviyo.reporting_api.partial_failure",
            integrationId: ctx.integrationId,
            reportType: fetcher.name,
            message: err instanceof Error ? err.message : String(err),
          }));
        }
      }
    }

    const records: Record<string, unknown>[] = [summaryRecord, ...dailyRecords, ...reportRecords];

    // ========================================================================
    // Step 7 (Story 12.8): Dev-mode runtime assertion for record-type contract
    // ========================================================================
    //
    // Reporting API records MUST NOT have a `date` field. This catches
    // regressions where a developer accidentally spreads a dailyOverview-shaped
    // record into a campaign/flow/form/segment record.
    //
    // H4 fix: fail-closed dev detection. In Cloudflare Workers production there
    // is no `process` global at all, so the `process` lookup returns undefined
    // and the assertion skips — protecting production from both the CPU cost
    // (~5-20µs per fetch) and the crash-on-violation risk.
    // In vitest we empirically verified `process.env.NODE_ENV === "test"` and
    // `process.env.VITEST === "true"`, so the assertion DOES fire in tests.
    // In `wrangler dev` (Miniflare) `process` is also undefined, so the
    // assertion skips there too — use the fixture tests to exercise the
    // contract during local dev.
    //
    // We access `process` via globalThis (not a bare reference) because the
    // integrations package does not include @types/node in its tsconfig.
    const nodeProcess = (globalThis as { process?: { env?: Record<string, string | undefined> } }).process;
    const isDev = nodeProcess !== undefined && nodeProcess.env?.NODE_ENV !== "production";
    if (isDev) {
      for (const record of records) {
        const reportType = record._reportType;
        if (
          (reportType === "campaign" || reportType === "flow" || reportType === "form" || reportType === "segment") &&
          "date" in record
        ) {
          throw new Error(
            `Klaviyo adapter contract violation: ${String(reportType)} record must not contain a 'date' field. ` +
            `Reporting API records are window totals, not daily series. Use _reportType='dailyOverview' for daily data.`
          );
        }
      }
    }

    return adapterOk({
      platform: "klaviyo",
      accountId: ctx.accountId || ctx.integrationId,
      dateRange,
      records,
      fetchedAt: new Date(),
      recordCount: records.length,
    });
  },

  async validateConnection(
    _ctx: AdapterContext,
    credentials: OAuthCredentials
  ): Promise<AdapterResult<boolean>> {
    const result = await fetchKlaviyoAccountsWithDetail(credentials.accessToken);

    if (!result.account) {
      // Map errorCode to appropriate AdapterErrorCode for accurate diagnostics
      const code = (result.errorCode === "permission_denied" ? "permission_denied"
        : result.errorCode === "rate_limited" ? "rate_limited"
        : result.errorCode === "network_error" ? "network_error"
        : "token_expired") as import("../adapter").AdapterErrorCode;
      const retryable = code === "rate_limited" || code === "network_error";
      return adapterErr({
        code,
        message: `Klaviyo connection validation failed (${result.errorCode ?? "unknown"})`,
        retryable,
      });
    }

    return adapterOk(true);
  },

  async refreshToken(
    _ctx: AdapterContext,
    credentials: OAuthCredentials
  ): Promise<AdapterResult<RefreshedTokens>> {
    if (!credentials.refreshToken) {
      return adapterErr({
        code: "token_invalid",
        message: "No refresh token available for Klaviyo",
        retryable: false,
      });
    }

    // Client ID and secret must come from env — passed through ctx.env
    const env = _ctx.env as unknown as Record<string, string>;
    const clientId = env.KLAVIYO_CLIENT_ID || "";
    const clientSecret = env.KLAVIYO_CLIENT_SECRET || "";

    if (!clientId || !clientSecret) {
      return adapterErr({
        code: "token_invalid",
        message: "Missing KLAVIYO_CLIENT_ID or KLAVIYO_CLIENT_SECRET env bindings",
        retryable: false,
      });
    }

    try {
      const tokenResponse = await refreshKlaviyoToken(
        credentials.refreshToken,
        clientId,
        clientSecret
      );

      return adapterOk({
        accessToken: tokenResponse.access_token,
        refreshToken: tokenResponse.refresh_token,
        expiresAt: new Date(Date.now() + tokenResponse.expires_in * 1000),
      });
    } catch (err) {
      return adapterErr({
        code: "token_expired",
        message: sanitizeErrorMessage(
          `Klaviyo token refresh failed: ${err instanceof Error ? err.message : String(err)}`
        ),
        retryable: false,
      });
    }
  },

  /**
   * Catalog discovery (Story 31.8). Klaviyo is single-tenant
   * (`oauthGrantScope: "single-tenant"`, registry): GET /api/accounts/
   * returns exactly 1 account per token, so the result is always a
   * length-1 array — the one account IS the full catalog. No pagination
   * exists on this endpoint; dedupe-by-id holds by construction
   * (`fetchKlaviyoAccountsWithDetail` takes `data.data[0]`, one emission).
   *
   * Error-code fidelity (AC #4): mirrors validateConnection's mapping
   * (plus a provider_unavailable passthrough for 5xx) so the 31-24
   * discovery cron can distinguish dead tokens (NOT retryable) from
   * transient failures. A 200-with-empty-`data` stays an
   * `invalid_response` ERROR, never `adapterOk([])` — for a single-tenant
   * platform the tenant itself is always discoverable with a healthy
   * token, so an empty catalog can only mean a broken response.
   */
  async discoverAccounts(
    _ctx: AdapterContext,
    credentials: OAuthCredentials
  ): Promise<AdapterResult<AccountInfo[]>> {
    const result = await fetchKlaviyoAccountsWithDetail(credentials.accessToken);

    if (!result.account) {
      // Map safeFetch errorCode → AdapterErrorCode (same pattern as
      // validateConnection above, plus a provider_unavailable passthrough:
      // safeFetch maps 5xx → "provider_unavailable", and collapsing a
      // Klaviyo outage into "invalid_response" would reintroduce exactly
      // the blanket-code problem this story removes — code review 31.8).
      // Absent detail (200-empty-`data`) and parse failures stay
      // "invalid_response".
      const code: AdapterErrorCode =
        result.errorCode === "permission_denied" ? "permission_denied"
        : result.errorCode === "rate_limited" ? "rate_limited"
        : result.errorCode === "network_error" ? "network_error"
        : result.errorCode === "provider_unavailable" ? "provider_unavailable"
        : result.errorCode === "token_expired" || result.errorCode === "token_invalid" ? "token_expired"
        : "invalid_response";
      // 401/403 must NOT be advertised as retryable — a cron retrying a
      // dead token just burns Klaviyo's 15 req/min budget.
      const retryable =
        code === "rate_limited" ||
        code === "network_error" ||
        code === "provider_unavailable" ||
        code === "invalid_response";
      return adapterErr({
        code,
        message: `Failed to discover Klaviyo account (${result.errorCode ?? "empty_or_invalid_response"})`,
        retryable,
      });
    }

    const accountInfo = result.account;

    return adapterOk([
      {
        id: accountInfo.accountId,
        name: accountInfo.accountName,
        platform: "klaviyo" as const,
        metadata: {
          timezone: accountInfo.timezone,
          currency: accountInfo.currency,
          isTestAccount: accountInfo.isTestAccount,
        },
        // Single source of truth: exported constant from oauth.ts (AC #2).
        scopes: [...KLAVIYO_SCOPES],
      },
    ]);
  },

  getAvailableMetrics(): MetricDefinition[] {
    return KLAVIYO_METRICS;
  },

  getAvailableDimensions(): DimensionDefinition[] {
    return KLAVIYO_DIMENSIONS;
  },
};
